import {
  LayoutDashboard,
  Users,
  Building2,
  Handshake,
  Mail,
  CheckSquare,
  CalendarDays,
  FileText,
  Search,
  BarChart3,
  Upload,
  Tags,
  UsersRound,
  Settings,
  BookOpen,
  Microscope,
  Zap,
  RefreshCw,
  Headphones,
  UserPlus,
  FileInput,
  Package,
  TrendingUp,
  Megaphone,
  GitBranch,
  CreditCard,
  Receipt,
  ListChecks,
  ShieldCheck,
  Map,
  Webhook,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { getUserInfo } from '../../lib/auth';

interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const sections: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Leads',
    items: [
      { id: 'leads', label: 'Leads', icon: UserPlus },
      { id: 'web-forms', label: 'Web Forms', icon: FileInput },
    ],
  },
  {
    label: 'Records',
    items: [
      { id: 'contacts', label: 'Contacts', icon: Users },
      { id: 'organizations', label: 'Organizations', icon: Building2 },
      { id: 'deals', label: 'Deals', icon: Handshake },
      { id: 'interactions', label: 'Inbox', icon: Mail },
    ],
  },
  {
    label: 'Sales',
    items: [
      { id: 'products', label: 'Products', icon: Package },
      { id: 'quotes', label: 'Quotes', icon: FileText },
      { id: 'forecasting', label: 'Forecasting', icon: TrendingUp },
    ],
  },
  {
    label: 'Service',
    items: [
      { id: 'cases', label: 'Cases', icon: Headphones },
    ],
  },
  {
    label: 'Work',
    items: [
      { id: 'activity-queue', label: 'My Queue', icon: ListChecks },
      { id: 'tasks', label: 'Tasks', icon: CheckSquare },
      { id: 'renewals', label: 'Renewals', icon: RefreshCw },
      { id: 'playbooks', label: 'Playbooks', icon: BookOpen },
      { id: 'agenda', label: 'Agenda', icon: CalendarDays },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
      { id: 'sequences', label: 'Sequences', icon: GitBranch },
      { id: 'email-templates', label: 'Email Templates', icon: Mail },
    ],
  },
  {
    label: 'Commerce',
    items: [
      { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
      { id: 'invoices', label: 'Invoices', icon: Receipt },
    ],
  },
  {
    label: 'Automation',
    items: [
      { id: 'workflows', label: 'Workflows', icon: Zap },
    ],
  },
  {
    label: 'Intel',
    items: [
      { id: 'dossiers', label: 'Dossiers', icon: BookOpen },
      { id: 'research', label: 'Research', icon: Microscope },
      { id: 'reports', label: 'Reports', icon: BarChart3 },
    ],
  },
  {
    label: 'Data',
    items: [
      { id: 'data-quality', label: 'Data Quality', icon: ShieldCheck },
      { id: 'territories', label: 'Territories', icon: Map },
    ],
  },
  {
    label: 'Developers',
    items: [
      { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    ],
  },
  {
    label: 'Admin',
    items: [
      { id: 'import', label: 'Import', icon: Upload },
      { id: 'tags', label: 'Tags', icon: Tags },
      { id: 'team', label: 'Team', icon: UsersRound },
      { id: 'settings', label: 'Settings', icon: Settings },
    ],
  },
];

function UserFooter() {
  const info = getUserInfo();
  const email = info?.email ?? '';
  const displayName = info?.name ?? email ?? 'User';
  const initial = (displayName[0] ?? 'U').toUpperCase();

  return (
    <div className="border-t border-sidebar-border px-3 py-3">
      <div className="flex items-center gap-2.5 rounded-md px-2 py-1.5">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-xs font-medium text-white">
          {initial}
        </div>
        <div className="flex-1 truncate">
          <div className="truncate text-xs font-medium text-sidebar-text-active">{displayName}</div>
          {displayName !== email && email && (
            <div className="truncate text-2xs text-sidebar-text">{email}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Sidebar({ currentView, onNavigate }: SidebarProps) {
  return (
    <aside className="flex h-screen w-60 flex-col bg-sidebar-bg">
      {/* Logo / Brand */}
      <div className="flex h-14 items-center gap-2.5 border-b border-sidebar-border px-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600">
          <span className="text-sm font-bold text-white">Z</span>
        </div>
        <span className="text-sm font-semibold text-sidebar-text-active">Zachary CRM</span>
      </div>

      {/* Search shortcut */}
      <div className="px-3 pt-4 pb-2">
        <button
          onClick={() => onNavigate('search')}
          className={cn(
            'flex w-full items-center gap-2 rounded-md border border-sidebar-border',
            'px-3 py-1.5 text-xs text-sidebar-text transition-colors hover:border-slate-500',
          )}
        >
          <Search className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">Search...</span>
          <kbd className="rounded bg-sidebar-hover px-1.5 py-0.5 text-2xs font-medium text-sidebar-text">
            Ctrl+K
          </kbd>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {sections.map((section) => (
          <div key={section.label} className="mb-4">
            <div className="section-label">{section.label}</div>
            {section.items.map((item) => {
              const isActive =
                currentView === item.id ||
                (item.id === 'contacts' && currentView === 'contact-detail') ||
                (item.id === 'organizations' && currentView === 'org-detail');
              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={cn(
                    isActive ? 'sidebar-item-active' : 'sidebar-item',
                    'w-full',
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <UserFooter />
    </aside>
  );
}
